import { call, put, takeLatest, take, race, select } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import {
    createChatSessionRequest,
    createChatSessionSuccess,
    createChatSessionFailure,
    setChatSessionId,
} from "../slice/chatSessionSlice";
import { createChatSessionApi } from "@src/api/apiService";
import { PatientContext } from "../slice/patientContextSlice";
import { RootState } from "../rootReducer";
import { getApiError } from "@src/utils/apiError";

export function* createChatSession(action: PayloadAction<{ partner_project_id?: string; current_consultation_id?: string; latest_consultation_id?: string; total_consultation_count?: number; no_context?: boolean } | undefined>): Generator<any, void, any> {
    const controller = new AbortController();

    // If no explicit context was forwarded by the caller, read it from the store
    // so every createChatSessionRequest() automatically attaches the active patient context.
    // Skip this when no_context is true — the caller explicitly wants a plain session
    // (e.g. fallback after a patient-context session creation failure).
    let contextPayload: { partner_project_id?: string; current_consultation_id?: string; latest_consultation_id?: string; total_consultation_count?: number } | undefined = undefined;
    if (!action.payload?.no_context) {
        contextPayload = action.payload;
        if (!contextPayload) {
            const stored: PatientContext | null = yield select((state: RootState) => state.patientContext.context);
            if (stored) {
                contextPayload = {
                    partner_project_id: stored.partnerProjectId,
                    current_consultation_id: stored.currentConsultationId,
                    total_consultation_count: stored.totalConsultationCount,
                };
            }
        }
    }

    // Visibility for the "session created without patient context" bug: log every
    // creation's resolved context so a contextless session that should have had one
    // leaves a trace in the console instead of failing silently.
    if (contextPayload && (contextPayload.partner_project_id || contextPayload.current_consultation_id)) {
        console.log("[createChatSession] creating session WITH patient context:", contextPayload);
    } else {
        console.log(
            "[createChatSession] creating session with NO patient context.",
            { explicitNoContext: !!action.payload?.no_context, callerPayload: action.payload }
        );
    }

    try {
        // Race the API call against the user navigating to an existing session.
        // If setChatSessionId fires first, abort the HTTP request so the backend
        // doesn't create an empty ghost session.
        const { response, navigated } = yield race({
            response: call(createChatSessionApi, contextPayload, controller.signal),
            navigated: take(setChatSessionId.type),
        });

        if (navigated) {
            controller.abort();
            return;
        }

        yield put(createChatSessionSuccess(response.data));
    } catch (error: unknown) {

        /*
         * -----------------------------------------
         * INTENTIONAL REQUEST CANCELLATION
         * -----------------------------------------
         *
         * Don't show this to the user.
         */

        if (
            error instanceof Error &&
            "code" in error &&
            (error as { code?: string }).code === "ERR_CANCELED"
        ) {
            return;
        }


        const apiError = getApiError(error);

        yield put(
            createChatSessionFailure(
                apiError.message
            )
        );
    }
}

// takeLatest: if createChatSessionRequest fires again while one is in flight,
// the previous saga (and its HTTP request via AbortController) is cancelled.
export function* chatSessionSaga() {
    yield takeLatest(createChatSessionRequest.type, createChatSession);
}
